import { createTheme, Theme, ThemeOptions } from "@mui/material";

export type PlatformThemeMode = "light" | "dark";
export type PlatformThemeDensity = "compact" | "standard" | "comfortable";
export type PlatformThemeTonalOffset = "subtle" | "standard" | "vivid";
export type PlatformThemeContrastLevel = "comfortable" | "accessible" | "strict";

export interface PlatformThemeRecord {
    id?: string;
    name?: string;
    label?: string;
    active?: boolean;
    is_default?: boolean;
    mode?: PlatformThemeMode | string;
    primary_seed_color?: string;
    secondary_seed_color?: string;
    neutral_seed_color?: string;
    background_seed_color?: string;
    success_seed_color?: string;
    warning_seed_color?: string;
    error_seed_color?: string;
    info_seed_color?: string;
    tonal_offset?: PlatformThemeTonalOffset | string;
    contrast_level?: PlatformThemeContrastLevel | string;
    density?: PlatformThemeDensity | string;
    radius_base?: string | number;
    spacing_scale?: string | number;
    font_family?: string;
    font_size_base?: string | number;
    advanced?: boolean | string;
    palette_overrides_json?: string | Record<string, unknown> | null;
    component_overrides_json?: string | ThemeOptions | null;
    show_logo_wordmark?: boolean | string;
    logo_mark_url?: string | null;
    logo_wordmark_url?: string | null;
}

export interface PlatformThemeTokens {
    id: string;
    name: string;
    label: string;
    mode: PlatformThemeMode;
    density: PlatformThemeDensity;
    radiusBase: number;
    spacingUnit: number;
    fontFamily: string;
    fontSizeBase: number;
    colors: {
        primary: ColorScale;
        secondary: ColorScale;
        success: ColorScale;
        warning: ColorScale;
        error: ColorScale;
        info: ColorScale;
        background: string;
        surface: string;
        surfaceRaised: string;
        textPrimary: string;
        textSecondary: string;
        textDisabled: string;
        accentOnSurface: string;
        errorOnSurface: string;
        successOnSurface: string;
        warningOnSurface: string;
        infoOnSurface: string;
        accentOnSecondary: string;
        accentOnTint: string;
        tintBackground: string;
        selectedBackground: string;
        textOnSelected: string;
        avatarBackground: string;
        avatarText: string;
        textMessage: string;
        border: string;
        borderInput: string;
        actionHover: string;
        actionSelected: string;
        actionDisabled: string;
        actionDisabledBackground: string;
        focus: string;
        appbarBackground: string;
        appbarText: string;
        menuBackground: string;
        menuText: string;
        scrollbarTrack: string;
        scrollbarThumb: string;
        scrollbarThumbHover: string;
    };
    logos: {
        markUrl?: string | null;
        wordmarkUrl?: string | null;
    };
    componentOverrides: ThemeOptions;
}

interface ColorScale {
    light: string;
    main: string;
    dark: string;
    contrastText: string;
}

interface RgbColor {
    r: number;
    g: number;
    b: number;
}

const DEFAULT_FONT_FAMILY = "Inter, Roboto, Arial, sans-serif";
const WHITE = "#FFFFFF";
const BLACK = "#111827";
const HEX_COLOR_PATTERN = /^#[0-9a-fA-F]{6}$/;

export const FALLBACK_PLATFORM_THEME: PlatformThemeRecord = {
    id: "fallback-primum-light",
    name: "primum_light",
    label: "Primum Light",
    mode: "light",
    primary_seed_color: "#D71920",
    secondary_seed_color: "#3F3A3C",
    neutral_seed_color: "#4B5563",
    background_seed_color: "#ECEEF1",
    tonal_offset: "standard",
    contrast_level: "accessible",
    density: "standard",
    radius_base: 8,
    spacing_scale: 1,
    font_family: DEFAULT_FONT_FAMILY,
    font_size_base: 14,
};

const clamp = (value: number, min = 0, max = 255) => Math.min(max, Math.max(min, value));
const clampRatio = (value: number) => Math.min(1, Math.max(0, value));

const isHexColor = (value: unknown): value is string =>
    typeof value === "string" && HEX_COLOR_PATTERN.test(value.trim());

const colorOr = (value: unknown, fallback: string) =>
    isHexColor(value) ? value.trim().toUpperCase() : fallback;

const numberOr = (value: unknown, fallback: number, min: number, max: number) => {
    const parsed = Number(value);
    return Number.isFinite(parsed) ? Math.min(max, Math.max(min, parsed)) : fallback;
};

const parseJsonObject = <T extends object>(value: unknown): T | undefined => {
    if (!value) return undefined;
    if (typeof value === "object" && !Array.isArray(value)) return value as T;
    if (typeof value !== "string" || !value.trim()) return undefined;

    try {
        const parsed = JSON.parse(value);
        return parsed && typeof parsed === "object" && !Array.isArray(parsed) ? parsed as T : undefined;
    } catch (_) {
        return undefined;
    }
};

const hexToRgb = (hex: string): RgbColor => ({
    r: parseInt(hex.slice(1, 3), 16),
    g: parseInt(hex.slice(3, 5), 16),
    b: parseInt(hex.slice(5, 7), 16),
});

const rgbToHex = ({ r, g, b }: RgbColor) =>
    `#${[r, g, b].map((channel) => clamp(Math.round(channel)).toString(16).padStart(2, "0")).join("")}`.toUpperCase();

const mix = (color: string, target: string, amount: number) => {
    const sourceRgb = hexToRgb(color);
    const targetRgb = hexToRgb(target);
    const ratio = clampRatio(amount);

    return rgbToHex({
        r: sourceRgb.r + (targetRgb.r - sourceRgb.r) * ratio,
        g: sourceRgb.g + (targetRgb.g - sourceRgb.g) * ratio,
        b: sourceRgb.b + (targetRgb.b - sourceRgb.b) * ratio,
    });
};

const alpha = (color: string, opacity: number) => {
    const { r, g, b } = hexToRgb(color);
    return `rgba(${r}, ${g}, ${b}, ${clampRatio(opacity)})`;
};

const relativeLuminance = (hex: string) => {
    const { r, g, b } = hexToRgb(hex);
    const toLinear = (channel: number) => {
        const value = channel / 255;
        return value <= 0.03928 ? value / 12.92 : Math.pow((value + 0.055) / 1.055, 2.4);
    };

    return 0.2126 * toLinear(r) + 0.7152 * toLinear(g) + 0.0722 * toLinear(b);
};

export const contrastRatio = (a: string, b: string) => {
    const lighter = Math.max(relativeLuminance(a), relativeLuminance(b));
    const darker = Math.min(relativeLuminance(a), relativeLuminance(b));
    return (lighter + 0.05) / (darker + 0.05);
};

export const ensureContrast = (foreground: string, background: string, minimum = 4.5): string => {
    if (contrastRatio(foreground, background) >= minimum) return foreground;
    const candidates = [WHITE, "#000000"].filter((target) => contrastRatio(target, background) >= minimum);
    if (!candidates.length) return contrastText(background, "accessible");
    const adjusted = candidates.map((target) => {
        let low = 0;
        let high = 1;
        for (let step = 0; step < 16; step += 1) {
            const middle = (low + high) / 2;
            if (contrastRatio(mix(foreground, target, middle), background) >= minimum) high = middle;
            else low = middle;
        }
        return { color: mix(foreground, target, high), amount: high };
    });
    return adjusted.sort((a, b) => a.amount - b.amount)[0].color;
};

const compositeOverlay = (background: string, overlay: string): string => {
    const rgba = overlay.match(/^rgba\(\s*(\d+)\s*,\s*(\d+)\s*,\s*(\d+)\s*,\s*([\d.]+)\s*\)$/);
    if (!rgba) return isHexColor(overlay) ? overlay : background;
    return mix(background, rgbToHex({ r: Number(rgba[1]), g: Number(rgba[2]), b: Number(rgba[3]) }), Number(rgba[4]));
};

export const ensureContrastAcross = (foreground: string, backgrounds: string[], minimum = 4.5): string => {
    if (backgrounds.every((background) => contrastRatio(foreground, background) >= minimum)) return foreground;
    const candidates = [WHITE, "#000000"].filter((target) => backgrounds.every((background) => contrastRatio(target, background) >= minimum));
    const adjusted = candidates.map((target) => {
        let low = 0;
        let high = 1;
        for (let step = 0; step < 16; step += 1) {
            const middle = (low + high) / 2;
            if (backgrounds.every((background) => contrastRatio(mix(foreground, target, middle), background) >= minimum)) high = middle;
            else low = middle;
        }
        return { color: mix(foreground, target, high), amount: high };
    });
    return adjusted.sort((a, b) => a.amount - b.amount)[0]?.color || foreground;
};

const contrastText = (background: string, level: PlatformThemeContrastLevel) => {
    const whiteContrast = contrastRatio(background, WHITE);
    const blackContrast = contrastRatio(background, BLACK);
    const minimum = level === "strict" ? 7 : level === "accessible" ? 4.5 : 3;

    if (whiteContrast >= minimum || blackContrast >= minimum) {
        return whiteContrast >= blackContrast ? WHITE : BLACK;
    }

    return whiteContrast >= blackContrast ? WHITE : BLACK;
};

const normalizeMode = (value: unknown): PlatformThemeMode => value === "dark" ? "dark" : "light";
const normalizeDensity = (value: unknown): PlatformThemeDensity =>
    value === "compact" || value === "comfortable" ? value : "standard";
const normalizeTonalOffset = (value: unknown): PlatformThemeTonalOffset =>
    value === "subtle" || value === "vivid" ? value : "standard";
const normalizeContrastLevel = (value: unknown): PlatformThemeContrastLevel =>
    value === "comfortable" || value === "strict" ? value : "accessible";
const enabled = (value: unknown) => value === true || value === "true" || value === 1 || value === "1";

const tonalAmounts = (offset: PlatformThemeTonalOffset) => {
    switch (offset) {
        case "subtle": return { light: 0.14, dark: 0.16 };
        case "vivid": return { light: 0.28, dark: 0.34 };
        default: return { light: 0.2, dark: 0.24 };
    }
};

const buildColorScale = (
    seed: string,
    mode: PlatformThemeMode,
    tonalOffset: PlatformThemeTonalOffset,
    contrastLevel: PlatformThemeContrastLevel
): ColorScale => {
    const amounts = tonalAmounts(tonalOffset);
    const main = seed;
    const light = mix(main, WHITE, mode === "dark" ? amounts.light + 0.08 : amounts.light);
    const dark = mix(main, "#000000", mode === "dark" ? amounts.dark + 0.06 : amounts.dark);

    return {
        light,
        main,
        dark,
        contrastText: contrastText(main, contrastLevel),
    };
};

const disabledTextColor = (backgrounds: string[]): string => {
    const [background] = backgrounds;
    const target = [WHITE, "#000000"].sort((a, b) =>
        Math.min(...backgrounds.map((color) => contrastRatio(b, color))) -
        Math.min(...backgrounds.map((color) => contrastRatio(a, color)))
    )[0];
    let low = 0;
    let high = 1;
    for (let step = 0; step < 16; step += 1) {
        const middle = (low + high) / 2;
        if (backgrounds.every((color) => contrastRatio(mix(background, target, middle), color) >= 2.8)) high = middle;
        else low = middle;
    }
    return mix(background, target, high);
};

const buildSurfaces = (mode: PlatformThemeMode, backgroundSeed: string, neutralSeed: string) => {
    if (mode === "dark") {
        const background = colorOr(backgroundSeed, "#09090B");
        const surface = mix(background, WHITE, 0.06);
        const surfaceRaised = mix(background, WHITE, 0.1);
        return {
            background,
            surface,
            surfaceRaised,
            textPrimary: "#F4F4F5",
            textSecondary: mix("#F4F4F5", background, 0.34),
            textDisabled: disabledTextColor([background, surface, surfaceRaised]),
            border: mix(background, WHITE, 0.18),
            neutral: neutralSeed,
        };
    }

    const background = colorOr(backgroundSeed, "#ECEEF1");
    const surface = mix(background, WHITE, 0.6);
    const surfaceRaised = WHITE;
    return {
        background,
        // Was mix(..., 0.86): pushed surface close enough to pure white that it was
        // nearly indistinguishable from the page background (both already near-white),
        // so cards/headers blended into the page in light mode. 0.6 keeps surface
        // lighter than the background (still reads as a "raised" card) while leaving
        // enough gap to actually see the edge, mirroring how dark mode's much smaller
        // ratio (0.06) still produces a clearly visible step up from its background.
        surface,
        surfaceRaised,
        textPrimary: mix(neutralSeed, "#000000", 0.5),
        textSecondary: mix(neutralSeed, background, 0.18),
        textDisabled: disabledTextColor([background, surface, surfaceRaised]),
        border: mix(background, neutralSeed, 0.16),
        neutral: neutralSeed,
    };
};

const deepMerge = (target: Record<string, any>, source?: Record<string, any>): Record<string, any> => {
    if (!source) return target;

    Object.entries(source).forEach(([key, value]) => {
        if (value && typeof value === "object" && !Array.isArray(value)) {
            target[key] = deepMerge({ ...(target[key] || {}) }, value as Record<string, any>);
            return;
        }

        target[key] = value;
    });

    return target;
};

export const buildPlatformThemeTokens = (record?: PlatformThemeRecord | null): PlatformThemeTokens => {
    const source = { ...FALLBACK_PLATFORM_THEME, ...(record || {}) };
    const mode = normalizeMode(source.mode);
    const density = normalizeDensity(source.density);
    const tonalOffset = normalizeTonalOffset(source.tonal_offset);
    const contrastLevel = normalizeContrastLevel(source.contrast_level);
    const spacingScale = numberOr(source.spacing_scale, 1, 0.75, 1.5);
    const radiusBase = numberOr(source.radius_base, 8, 0, 24);
    const fontSizeBase = numberOr(source.font_size_base, 14, 12, 18);

    const primarySeed = colorOr(source.primary_seed_color, "#D71920");
    const secondarySeed = colorOr(source.secondary_seed_color, "#3F3A3C");
    const neutralSeed = colorOr(source.neutral_seed_color, mode === "dark" ? "#A1A1AA" : "#4B5563");
    const backgroundSeed = colorOr(source.background_seed_color, mode === "dark" ? "#09090B" : "#ECEEF1");
    const surfaces = buildSurfaces(mode, backgroundSeed, neutralSeed);
    const useAdvancedOverrides = enabled(source.advanced);
    const paletteOverrides = useAdvancedOverrides
        ? parseJsonObject<Record<string, any>>(source.palette_overrides_json)
        : undefined;

    const primary = buildColorScale(primarySeed, mode, tonalOffset, contrastLevel);
    const secondary = buildColorScale(secondarySeed, mode, tonalOffset, contrastLevel);
    const success = buildColorScale(colorOr(source.success_seed_color, mode === "dark" ? "#4ADE80" : "#16A34A"), mode, tonalOffset, contrastLevel);
    const warning = buildColorScale(colorOr(source.warning_seed_color, mode === "dark" ? "#FACC15" : "#A16207"), mode, tonalOffset, contrastLevel);
    const error = buildColorScale(colorOr(source.error_seed_color, mode === "dark" ? "#FB923C" : "#B93C0E"), mode, tonalOffset, contrastLevel);
    const info = buildColorScale(colorOr(source.info_seed_color, "#2563EB"), mode, tonalOffset, contrastLevel);

    const colors = deepMerge({
        primary,
        secondary,
        success,
        warning,
        error,
        info,
        background: surfaces.background,
        surface: surfaces.surface,
        surfaceRaised: surfaces.surfaceRaised,
        textPrimary: surfaces.textPrimary,
        textSecondary: surfaces.textSecondary,
        textDisabled: surfaces.textDisabled,
        accentOnSurface: ensureContrast(primary.main, mode === "dark" ? surfaces.surfaceRaised : surfaces.surface),
        errorOnSurface: ensureContrastAcross(error.main, [surfaces.surface, surfaces.surfaceRaised, surfaces.background]),
        successOnSurface: ensureContrastAcross(success.main, [surfaces.surface, surfaces.surfaceRaised, surfaces.background]),
        warningOnSurface: ensureContrastAcross(warning.main, [surfaces.surface, surfaces.surfaceRaised, surfaces.background]),
        infoOnSurface: ensureContrastAcross(info.main, [surfaces.surface, surfaces.surfaceRaised, surfaces.background]),
        accentOnSecondary: ensureContrast(primary.main, secondary.main),
        accentOnTint: ensureContrast(primary.main, mix(surfaces.surface, primary.main, 0.16)),
        tintBackground: mix(surfaces.surface, primary.main, 0.16),
        selectedBackground: mix(surfaces.surface, primary.main, mode === "dark" ? 0.28 : 0.14),
        textOnSelected: surfaces.textPrimary,
        avatarBackground: secondary.main,
        avatarText: contrastText(secondary.main, contrastLevel),
        textMessage: ensureContrast(surfaces.textSecondary, surfaces.surface),
        border: surfaces.border,
        borderInput: ensureContrastAcross(surfaces.border, [surfaces.surface, surfaces.surfaceRaised, surfaces.background], 3),
        actionHover: alpha(primary.main, mode === "dark" ? 0.14 : 0.08),
        actionSelected: alpha(primary.main, mode === "dark" ? 0.22 : 0.12),
        actionDisabled: surfaces.textDisabled,
        actionDisabledBackground: alpha(surfaces.textPrimary, mode === "dark" ? 0.12 : 0.08),
        focus: surfaces.textPrimary,
        appbarBackground: mode === "dark" ? surfaces.surfaceRaised : WHITE,
        appbarText: mode === "dark" ? surfaces.textPrimary : contrastText(WHITE, contrastLevel),
        menuBackground: surfaces.surfaceRaised,
        menuText: surfaces.textPrimary,
        scrollbarTrack: mode === "dark" ? mix(surfaces.background, WHITE, 0.08) : mix(surfaces.background, neutralSeed, 0.06),
        scrollbarThumb: mode === "dark" ? mix(primary.main, surfaces.textPrimary, 0.18) : mix(primary.main, neutralSeed, 0.18),
        scrollbarThumbHover: mode === "dark" ? mix(primary.main, surfaces.textPrimary, 0.3) : mix(primary.main, "#000000", 0.12),
    }, paletteOverrides as Record<string, any> | undefined) as PlatformThemeTokens["colors"];

    const derivedColors: Pick<PlatformThemeTokens["colors"], "accentOnSurface" | "errorOnSurface" | "successOnSurface" | "warningOnSurface" | "infoOnSurface" | "borderInput" | "accentOnSecondary" | "accentOnTint" | "tintBackground" | "selectedBackground" | "textOnSelected" | "textSecondary" | "avatarBackground" | "avatarText" | "textMessage"> = {
        accentOnSurface: ensureContrastAcross(colors.primary.main, [colors.surface, colors.surfaceRaised, colors.background]),
        errorOnSurface: ensureContrastAcross(colors.error.main, [colors.surface, colors.surfaceRaised, colors.background]),
        successOnSurface: ensureContrastAcross(colors.success.main, [colors.surface, colors.surfaceRaised, colors.background]),
        warningOnSurface: ensureContrastAcross(colors.warning.main, [colors.surface, colors.surfaceRaised, colors.background]),
        infoOnSurface: ensureContrastAcross(colors.info.main, [colors.surface, colors.surfaceRaised, colors.background]),
        borderInput: ensureContrastAcross(colors.border, [colors.surface, colors.surfaceRaised, colors.background], 3),
        accentOnSecondary: ensureContrast(colors.primary.main, colors.secondary.main),
        tintBackground: mix(colors.surface, colors.primary.main, 0.16),
        selectedBackground: mix(colors.surface, colors.primary.main, mode === "dark" ? 0.28 : 0.14),
        accentOnTint: colors.accentOnTint,
        textOnSelected: colors.textOnSelected,
        textSecondary: colors.textSecondary,
        avatarBackground: colors.secondary.main,
        avatarText: contrastText(colors.secondary.main, contrastLevel),
        textMessage: ensureContrastAcross(colors.textSecondary, [
            colors.surface,
            compositeOverlay(colors.surface, colors.actionHover),
            compositeOverlay(colors.surface, colors.actionSelected),
        ]),
    };
    derivedColors.accentOnTint = ensureContrastAcross(colors.primary.main, [
        derivedColors.tintBackground,
        derivedColors.selectedBackground,
        compositeOverlay(colors.surface, colors.actionHover),
    ]);
    derivedColors.textOnSelected = ensureContrastAcross(colors.textPrimary, [derivedColors.selectedBackground]);
    derivedColors.textSecondary = ensureContrastAcross(colors.textSecondary, [colors.surface, colors.surfaceRaised, colors.background, derivedColors.tintBackground, derivedColors.selectedBackground]);
    Object.entries(derivedColors).forEach(([key, value]) => {
        if (!Object.prototype.hasOwnProperty.call(paletteOverrides || {}, key)) {
            (colors as unknown as Record<string, string>)[key] = value;
        }
    });

    return {
        id: source.id || FALLBACK_PLATFORM_THEME.id!,
        name: source.name || FALLBACK_PLATFORM_THEME.name!,
        label: source.label || source.name || FALLBACK_PLATFORM_THEME.label!,
        mode,
        density,
        radiusBase,
        spacingUnit: Math.round(8 * spacingScale * (density === "compact" ? 0.875 : density === "comfortable" ? 1.125 : 1)),
        fontFamily: source.font_family || DEFAULT_FONT_FAMILY,
        fontSizeBase,
        colors,
        logos: {
            markUrl: source.logo_mark_url,
            wordmarkUrl: source.logo_wordmark_url,
        },
        componentOverrides: useAdvancedOverrides ? parseJsonObject<ThemeOptions>(source.component_overrides_json) || {} : {},
    };
};

export const normalizePlatformTheme = (record?: PlatformThemeRecord | null): PlatformThemeRecord => {
    const tokens = buildPlatformThemeTokens(record);
    return {
        ...(record || {}),
        id: tokens.id,
        name: tokens.name,
        label: tokens.label,
        mode: tokens.mode,
        density: tokens.density,
        logo_mark_url: tokens.logos.markUrl,
        logo_wordmark_url: tokens.logos.wordmarkUrl,
    };
};

export const platformThemeCssVariables = (theme: PlatformThemeRecord) => {
    const tokens = buildPlatformThemeTokens(theme);
    const { colors } = tokens;

    return {
        "--prm-primary": colors.primary.main,
        "--prm-primary-light": colors.primary.light,
        "--prm-primary-dark": colors.primary.dark,
        "--prm-primary-contrast": colors.primary.contrastText,
        "--prm-secondary": colors.secondary.main,
        "--prm-secondary-light": colors.secondary.light,
        "--prm-secondary-dark": colors.secondary.dark,
        "--prm-bg": colors.background,
        "--prm-surface": colors.surface,
        "--prm-surface-raised": colors.surfaceRaised,
        "--prm-text": colors.textPrimary,
        "--prm-text-primary": colors.textPrimary,
        "--prm-text-muted": colors.textSecondary,
        "--prm-text-secondary": colors.textSecondary,
        "--prm-text-disabled": colors.textDisabled,
        "--prm-accent-on-surface": colors.accentOnSurface,
        "--prm-error-on-surface": colors.errorOnSurface,
        "--prm-success-on-surface": colors.successOnSurface,
        "--prm-warning-on-surface": colors.warningOnSurface,
        "--prm-info-on-surface": colors.infoOnSurface,
        "--prm-accent-on-secondary": colors.accentOnSecondary,
        "--prm-accent-on-tint": colors.accentOnTint,
        "--prm-tint-bg": colors.tintBackground,
        "--prm-selected-bg": colors.selectedBackground,
        "--prm-text-on-selected": colors.textOnSelected,
        "--prm-avatar-bg": colors.avatarBackground,
        "--prm-avatar-text": colors.avatarText,
        "--prm-text-message": colors.textMessage,
        "--prm-border": colors.border,
        "--prm-border-input": colors.borderInput,
        "--prm-success": colors.success.main,
        "--prm-alert": colors.warning.main,
        "--prm-warning": colors.warning.main,
        "--prm-error": colors.error.main,
        "--prm-info": colors.info.main,
        "--prm-focus": colors.focus,
        "--prm-action-hover": colors.actionHover,
        "--prm-action-selected": colors.actionSelected,
        "--prm-appbar-bg": colors.appbarBackground,
        "--prm-appbar-text": colors.appbarText,
        "--prm-menu-bg": colors.menuBackground,
        "--prm-menu-text": colors.menuText,
        "--prm-scrollbar-track": colors.scrollbarTrack,
        "--prm-scrollbar-thumb": colors.scrollbarThumb,
        "--prm-scrollbar-thumb-hover": colors.scrollbarThumbHover,
        "--prm-radius": `${tokens.radiusBase}px`,
        "--prm-spacing": `${tokens.spacingUnit}px`,
    };
};

export const createMuiThemeFromPlatformTheme = (record?: PlatformThemeRecord | null): Theme => {
    const tokens = buildPlatformThemeTokens(record);
    const { colors } = tokens;
    const baseTheme: ThemeOptions = {
        palette: {
            mode: tokens.mode,
            primary: colors.primary,
            secondary: colors.secondary,
            success: colors.success,
            warning: colors.warning,
            error: colors.error,
            info: colors.info,
            background: {
                default: colors.background,
                paper: colors.surface,
            },
            text: {
                primary: colors.textPrimary,
                secondary: colors.textSecondary,
                disabled: colors.textDisabled,
            },
            divider: colors.border,
            action: {
                hover: colors.actionHover,
                selected: colors.actionSelected,
                disabled: colors.actionDisabled,
                disabledBackground: colors.actionDisabledBackground,
                focus: alpha(colors.textPrimary, tokens.mode === "dark" ? 0.16 : 0.12),
            },
        },
        shape: {
            borderRadius: tokens.radiusBase,
        },
        spacing: tokens.spacingUnit,
        typography: {
            fontFamily: tokens.fontFamily,
            fontSize: tokens.fontSizeBase,
            button: {
                textTransform: "none",
                fontWeight: 700,
            },
        },
        components: {
            MuiButton: {
                defaultProps: { variant: "contained" },
                styleOverrides: {
                    root: { textTransform: "none", fontWeight: 700, borderRadius: tokens.radiusBase },
                    contained: {
                        boxShadow: "none",
                        "&:hover": { boxShadow: "none" },
                        "&.MuiButton-containedInherit": {
                            color: "var(--prm-text-primary)",
                            backgroundColor: "var(--prm-surface-raised)",
                            "&:hover": { backgroundColor: "var(--prm-surface-raised)" },
                            "&.Mui-focusVisible": { backgroundColor: "var(--prm-surface-raised)" },
                        },
                    },
                    outlined: {
                        "&.MuiButton-outlinedPrimary": {
                            color: "var(--prm-accent-on-surface)",
                            borderColor: `var(--prm-outlined-border, ${ensureContrastAcross(colors.primary.main, [colors.surface, colors.surfaceRaised], 3)})`,
                        },
                    },
                    text: { "&.MuiButton-textPrimary": { color: "var(--prm-accent-on-surface)" } },
                },
            },
            MuiButtonBase: {
                styleOverrides: {
                    root: {
                        "&.Mui-focusVisible, &:focus-visible": {
                            outline: "3px solid var(--prm-focus)",
                            outlineOffset: 2,
                        },
                    },
                },
            },
            MuiAppBar: {
                styleOverrides: {
                    root: {
                        backgroundColor: "var(--prm-appbar-bg)",
                        color: "var(--prm-appbar-text)",
                        borderRadius: "0px !important",
                    },
                },
            },
            MuiPaper: {
                styleOverrides: {
                    root: { backgroundImage: "none" },
                },
            },
            MuiTab: {
                styleOverrides: {
                    root: { "&.Mui-selected": { color: "var(--prm-accent-on-surface)" } },
                },
            },
            MuiChip: {
                styleOverrides: {
                    root: {
                        "&.MuiChip-outlined, &.MuiChip-colorPrimary": {
                            color: "var(--prm-accent-on-tint)",
                            backgroundColor: "var(--prm-tint-bg)",
                        },
                    },
                },
            },
            MuiAvatar: {
                styleOverrides: {
                    root: { backgroundColor: "var(--prm-avatar-bg)", color: "var(--prm-avatar-text)" },
                },
            },
            MuiOutlinedInput: {
                styleOverrides: {
                    root: {
                        "& .MuiOutlinedInput-notchedOutline": {
                            borderColor: "var(--prm-border-input)",
                        },
                        "&.Mui-disabled": {
                            backgroundColor: "var(--prm-surface-raised)",
                            "& .MuiOutlinedInput-notchedOutline": {
                                borderColor: "var(--prm-border)",
                            },
                        },
                        "&.Mui-focused:not(.Mui-error) .MuiOutlinedInput-notchedOutline": {
                            borderColor: "var(--prm-focus)",
                            borderWidth: 2,
                        },
                    },
                },
            },
            MuiFormHelperText: {
                styleOverrides: {
                    root: { "&.Mui-error": { color: "var(--prm-error-on-surface)" } },
                },
            },
            MuiFormLabel: {
                styleOverrides: {
                    root: { "&.Mui-error": { color: "var(--prm-error-on-surface)" } },
                },
            },
            MuiSwitch: {
                styleOverrides: {
                    root: {
                        "& .MuiSwitch-switchBase.Mui-disabled + .MuiSwitch-track": {
                            backgroundColor: "var(--prm-surface)",
                            opacity: 1,
                        },
                        "& .MuiSwitch-switchBase.Mui-disabled .MuiSwitch-thumb": {
                            backgroundColor: "var(--prm-text-primary)",
                        },
                        "& .MuiSwitch-switchBase.Mui-disabled.Mui-checked + .MuiSwitch-track": {
                            backgroundColor: "var(--prm-accent-on-surface)",
                            opacity: 1,
                        },
                        "& .MuiSwitch-switchBase.Mui-disabled.Mui-checked .MuiSwitch-thumb": {
                            backgroundColor: "var(--prm-surface-raised)",
                        },
                    },
                },
            },
            MuiCssBaseline: {
                styleOverrides: {
                    html: {
                        backgroundColor: "var(--prm-bg)",
                        scrollbarColor: "var(--prm-scrollbar-thumb) var(--prm-scrollbar-track)",
                        scrollbarWidth: "thin",
                    },
                    body: { backgroundColor: "var(--prm-bg)", color: "var(--prm-text-primary)" },
                    "#root": { backgroundColor: "var(--prm-bg)" },
                    "*": {
                        scrollbarWidth: "thin",
                        scrollbarColor: "var(--prm-scrollbar-thumb) var(--prm-scrollbar-track)",
                    },
                    "*:focus-visible": {
                        outline: "3px solid var(--prm-focus)",
                        outlineOffset: 2,
                    },
                    "*::-webkit-scrollbar": {
                        width: 10,
                        height: 10,
                    },
                    "*::-webkit-scrollbar-track": {
                        backgroundColor: "var(--prm-scrollbar-track)",
                    },
                    "*::-webkit-scrollbar-thumb": {
                        backgroundColor: "var(--prm-scrollbar-thumb)",
                        borderRadius: 999,
                        border: "2px solid var(--prm-scrollbar-track)",
                    },
                    "*::-webkit-scrollbar-thumb:hover": {
                        backgroundColor: "var(--prm-scrollbar-thumb-hover)",
                    },
                    "*::-webkit-scrollbar-corner": {
                        backgroundColor: "var(--prm-scrollbar-track)",
                    },
                },
            },
        },
    };

    return createTheme(deepMerge(baseTheme as Record<string, any>, tokens.componentOverrides as Record<string, any>) as ThemeOptions);
};
